import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
    Wifi,
    Truck,
    Headphones,
    Briefcase,
    ArrowRight,
    Sparkles,
    Activity,
    UserCog,
    LogIn,
    FileText,
    Wrench,
    DollarSign,
    Plug,
    Lock,
    AlertTriangle,
    Phone,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { GlassCard } from "@/components/ui/GlassCard";
import { StatCard } from "@/components/ui/StatCard";
import { PageHeader } from "@/components/ui/PageHeader";
import { NxBadge } from "@/components/ui/NxBadge";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet } from "@/lib/api";
import pt from "@/lib/i18n";
import { formatPtDate, greetingKey, formatRelative } from "@/lib/utils";
import { canAccess } from "@/lib/rbac";
import { AskNexusTelWidget } from "@/components/widgets/AskNexusTelWidget";
import { MorningBriefing } from "@/components/widgets/MorningBriefing";
import DemoBanner from "@/components/widgets/DemoBanner";

const QUICK_ACTIONS = [
    {
        key: "services",
        moduleKey: "services",
        label: pt.dashboard.quick.services,
        desc: pt.dashboard.quick.servicesDesc,
        icon: Wifi,
        to: "/servicos",
    },
    {
        key: "fieldOps",
        moduleKey: "fieldOps",
        label: pt.dashboard.quick.field,
        desc: pt.dashboard.quick.fieldDesc,
        icon: Truck,
        to: "/operacoes-campo",
    },
    {
        key: "cx",
        moduleKey: "cx",
        label: pt.dashboard.quick.tickets,
        desc: pt.dashboard.quick.ticketsDesc,
        icon: Headphones,
        to: "/servico-cliente",
    },
    {
        key: "sales",
        moduleKey: "sales",
        label: pt.dashboard.quick.sales,
        desc: pt.dashboard.quick.salesDesc,
        icon: Briefcase,
        to: "/vendas",
    },
];

const ACTION_ICONS = {
    "user.login": LogIn,
    "user.password_changed": Lock,
    "user.updated": UserCog,
    "user.created": UserCog,
    "user.profile_updated": UserCog,
    "service.created": Wifi,
    "service.activated": Wifi,
    "ticket.opened": Headphones,
    "ticket.resolved": Headphones,
    "document.uploaded": FileText,
    "invoice.generated": DollarSign,
    "integration.synced": Plug,
};

const ACTION_LABEL = pt.activityTypes ? {
    ...pt.activityTypes,
    "user.password_changed": pt.activityTypes["user.password_changed"] || "Palavra-passe alterada",
    "user.updated": pt.activityTypes["user.updated"] || "Utilizador actualizado",
    "user.created": pt.activityTypes["user.created"] || "Utilizador criado",
    "user.profile_updated": pt.activityTypes["user.profile_updated"] || "Perfil actualizado",
    "document.uploaded": pt.activityTypes["document.uploaded"] || "Documento carregado",
    "invoice.generated": pt.activityTypes["invoice.generated"] || "Factura gerada",
    "user.deleted": pt.activityTypes["user.deleted"] || "Utilizador eliminado",
} : {
    "user.login": pt.activityTypes?.["user.login"] || "—",
    "user.password_changed": pt.activityTypes?.["user.password_changed"] || "—",
    "user.updated": pt.activityTypes?.["user.updated"] || "—",
    "user.created": pt.activityTypes?.["user.created"] || "—",
    "user.profile_updated": pt.activityTypes?.["user.profile_updated"] || "—",
    "service.created": pt.activityTypes?.["service.created"] || "—",
    "service.activated": pt.activityTypes?.["service.activated"] || "—",
    "ticket.opened": pt.activityTypes?.["ticket.opened"] || "—",
    "ticket.resolved": pt.activityTypes?.["ticket.resolved"] || "—",
    "document.uploaded": pt.activityTypes?.["document.uploaded"] || "—",
    "invoice.generated": pt.activityTypes?.["invoice.generated"] || "—",
    "integration.synced": pt.activityTypes?.["integration.synced"] || "—",
    "user.deleted": pt.activityTypes?.["user.deleted"] || "—",
};

const Dashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [kpis, setKpis] = useState(null);
    const [activity, setActivity] = useState(null);
    const [retentionAlerts, setRetentionAlerts] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [k, a] = await Promise.all([
                    apiGet("/dashboard/kpis"),
                    apiGet("/dashboard/recent-activity"),
                ]);
                setKpis(k);
                setActivity(a);
            } catch (e) {
                setKpis([]);
                setActivity([]);
            }
            // Retention alerts (CX roles only)
            if (canAccess("tickets", user?.role) || canAccess("customers", user?.role)) {
                try {
                    const r = await apiGet("/churn-risk/retention-alerts");
                    setRetentionAlerts(r || []);
                } catch (e) { setRetentionAlerts([]); }
            } else {
                setRetentionAlerts([]);
            }
        })();
    }, [user?.role]);

    const firstName = user?.full_name?.split(" ")[0] || "";
    const greeting = pt.dashboard[greetingKey()];
    const today = formatPtDate(new Date());

    return (
        <div data-testid="dashboard-root">
            <DemoBanner user={user} />
            <PageHeader
                testId="dashboard-header"
                title={
                    <span>
                        {greeting},{" "}
                        <span className="text-[color:var(--copper-dark)]">{firstName}</span>.
                    </span>
                }
                subtitle={today}
                right={
                    <div className="hidden md:flex items-center gap-2 px-3.5 py-1.5 rounded-full glass-card" style={{ borderRadius: 999 }}>
                        <Activity size={14} className="text-[color:var(--success)]" />
                        <span className="font-body text-xs text-[color:var(--slate-mid)]">
                            {pt.dashboard.operationalStatus}
                        </span>
                    </div>
                }
            />

            {/* KPIs */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="kpi-grid">
                {kpis === null
                    ? [0, 1, 2, 3].map((i) => (
                          <GlassCard key={i} padding="p-6">
                              <Skeleton height="14px" width="50%" />
                              <div className="mt-4">
                                  <Skeleton height="36px" width="70%" />
                              </div>
                              <div className="mt-6">
                                  <Skeleton height="6px" width="100%" />
                              </div>
                          </GlassCard>
                      ))
                    : kpis.map((k, i) => (
                          <StatCard
                              key={k.key}
                              label={pt.kpiLabels?.[k.key] || k.label}
                              value={k.value}
                              format={k.format}
                              trend={k.trend}
                              index={i}
                              testId={`kpi-${k.key}`}
                          />
                      ))}
            </section>

            {/* Quick actions */}
            <section className="mb-8" data-testid="quick-actions">
                <h3 className="font-display text-xl text-[color:var(--slate-deep)] mb-3">
                    {pt.dashboard.quickActions}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {QUICK_ACTIONS.filter((a) => canAccess(a.moduleKey, user?.role)).map((a, i) => {
                        const Icon = a.icon;
                        return (
                            <motion.div
                                key={a.key}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 + i * 0.05, duration: 0.4 }}
                            >
                                <GlassCard
                                    hover
                                    padding="p-5"
                                    className="cursor-pointer h-full"
                                    onClick={() => navigate(a.to)}
                                    testId={`quick-action-${a.key}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="h-10 w-10 rounded-xl bg-[color:var(--copper-subtle)] grid place-items-center">
                                            <Icon size={20} className="text-[color:var(--copper-dark)]" strokeWidth={1.6} />
                                        </div>
                                        <ArrowRight
                                            size={16}
                                            className="text-[color:var(--slate-light)] mt-2"
                                        />
                                    </div>
                                    <p className="font-display text-base text-[color:var(--slate-deep)] mt-4">
                                        {a.label}
                                    </p>
                                    <p className="font-body text-xs text-[color:var(--slate-mid)] mt-1">
                                        {a.desc}
                                    </p>
                                </GlassCard>
                            </motion.div>
                        );
                    })}
                </div>
            </section>

            {/* Morning Briefing + Ask NexusTel */}
            {canAccess("analytics", user?.role) && (
                <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8" data-testid="executive-widgets">
                    <MorningBriefing />
                    <AskNexusTelWidget />
                </section>
            )}

            {/* Retention Alerts (CX-triggered) */}
            {retentionAlerts && retentionAlerts.length > 0 && (
                <section className="mb-8" data-testid="retention-alerts-section">
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.45 }}>
                        <GlassCard padding="p-6" style={{ borderColor: "rgba(192,57,43,0.32)", borderWidth: 1.5, background: "linear-gradient(180deg, rgba(255,247,237,0.78), rgba(255,242,235,0.55))" }}>
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "rgba(192,57,43,0.12)" }}>
                                        <AlertTriangle size={20} className="text-[color:var(--error)]" strokeWidth={1.8} />
                                    </div>
                                    <div>
                                        <h3 className="font-display text-lg text-[color:var(--slate-deep)] leading-tight">{pt.csat?.retentionAlerts || "Alertas de Retenção (CSAT)"}</h3>
                                        <p className="font-body text-xs text-[color:var(--slate-mid)]">{pt.common.csatChurnHint}</p>
                                    </div>
                                </div>
                                <NxBadge variant="error" testId="retention-alerts-count">{retentionAlerts.length} {pt.csat?.alerts || "alertas"}</NxBadge>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="retention-alerts-grid">
                                {retentionAlerts.slice(0, 6).map((a, i) => (
                                    <motion.div
                                        key={a.csat_id}
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.7 + i * 0.05 }}
                                        className="p-3 rounded-lg bg-white/70 border border-[color:var(--glass-border)]"
                                        data-testid={`retention-alert-${a.csat_id}`}
                                    >
                                        <div className="flex items-center justify-between mb-1.5">
                                            <p className="font-body text-sm font-medium text-[color:var(--slate-deep)] truncate flex-1">
                                                {a.customer?.full_name || "Cliente desconhecido"}
                                            </p>
                                            <span className="font-mono text-xs text-[color:var(--error)] font-medium">{a.score}/5★</span>
                                        </div>
                                        {a.customer?.phone && (
                                            <p className="font-mono text-[11px] text-[color:var(--slate-mid)] flex items-center gap-1 mb-1">
                                                <Phone size={10} />{a.customer.phone}
                                            </p>
                                        )}
                                        {a.comment && (
                                            <p className="font-body text-xs text-[color:var(--slate-mid)] italic line-clamp-2 mt-1">
                                                &ldquo;{a.comment}&rdquo;
                                            </p>
                                        )}
                                        {a.customer?.lifetime_value_aoa > 0 && (
                                            <p className="font-mono text-[10px] text-[color:var(--copper-dark)] mt-2">
                                                LTV: AOA {Number(a.customer.lifetime_value_aoa).toLocaleString("pt-PT")}
                                            </p>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </GlassCard>
                    </motion.div>
                </section>
            )}

            {/* Activity + AI Summary */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                    <GlassCard padding="p-6" testId="recent-activity">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-xl text-[color:var(--slate-deep)]">
                                {pt.dashboard.recentActivity}
                            </h3>
                            <NxBadge variant="neutral">
                                {activity?.length ?? 0} eventos
                            </NxBadge>
                        </div>
                        <ul className="space-y-1">
                            {activity === null
                                ? [1, 2, 3, 4, 5].map((i) => (
                                      <li key={i} className="py-2">
                                          <Skeleton height="40px" />
                                      </li>
                                  ))
                                : activity.length === 0
                                  ? <li className="py-6 text-center text-sm text-[color:var(--slate-mid)] font-body">
                                        Sem actividade recente.
                                    </li>
                                  : activity.map((log) => {
                                        const Icon = ACTION_ICONS[log.action] || Activity;
                                        const label = ACTION_LABEL[log.action] || log.action;
                                        return (
                                            <li
                                                key={log.id}
                                                className="flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-lg hover:bg-[color:var(--copper-subtle)] transition-colors"
                                                data-testid={`activity-${log.id}`}
                                            >
                                                <div className="h-9 w-9 rounded-full bg-[color:var(--bg-elevated)] border border-[color:var(--glass-border)] grid place-items-center flex-shrink-0">
                                                    <Icon size={15} className="text-[color:var(--copper-dark)]" strokeWidth={1.6} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-body text-sm text-[color:var(--slate-deep)] truncate">
                                                        <span className="font-medium">{log.actor_name}</span>{" "}
                                                        <span className="text-[color:var(--slate-mid)]">— {label}</span>
                                                    </p>
                                                </div>
                                                <span className="font-mono text-[11px] text-[color:var(--slate-light)] flex-shrink-0">
                                                    {formatRelative(log.created_at)}
                                                </span>
                                            </li>
                                        );
                                    })}
                        </ul>
                    </GlassCard>
                </div>

                {/* AI Summary placeholder */}
                <div>
                    <GlassCard
                        padding="p-6"
                        className="h-full"
                        testId="ai-summary"
                        style={{ borderColor: "var(--copper-glow)", borderWidth: 1.5 }}
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles size={18} className="text-[color:var(--copper)]" />
                            <h3 className="font-display text-lg text-[color:var(--slate-deep)]">
                                {pt.dashboard.aiSummary}
                            </h3>
                        </div>
                        <NxBadge variant="copper" className="mb-3">
                            {pt.dashboard.comingSoon}
                        </NxBadge>
                        <p className="font-body text-sm text-[color:var(--slate-mid)] leading-relaxed">
                            {pt.dashboard.aiSummaryPlaceholder}
                        </p>
                        <div className="mt-5 pt-5 border-t border-[color:var(--glass-border)]">
                            <div className="flex items-center gap-2">
                                <div className="h-2 w-2 rounded-full bg-[color:var(--copper)] animate-pulse" />
                                <span className="font-mono text-[11px] text-[color:var(--slate-mid)] uppercase tracking-[0.18em]">
                                    Claude Sonnet 4 · ligado
                                </span>
                            </div>
                        </div>
                    </GlassCard>
                </div>
            </section>
        </div>
    );
};

export default Dashboard;
